import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Add, Check, Close, Edit } from '@mui/icons-material';
import { Box, IconButton, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import { Table, usePagination } from '@frontend/features/common/components/Table';
import { ListLayout } from '@frontend/features/common/components/ListLayout';
import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';

import { TABLE_HEIGHT_WITH_AGENCIES_LAYOUT } from '@frontend/features/agencies/constants';
import { useGetCaseHandlersQuery } from '@frontend/features/case-handlers/queries';
import { HandlerModal } from '@frontend/features/case-handlers/components/HandlerModal';

import { CaseHandlerResponseDto } from '@bandido/shared/dtos';

export const IaCaseEventHandlersList = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedHandler, setSelectedHandler] = useState<CaseHandlerResponseDto>();
  const pagination = usePagination();

  const { data } = useGetCaseHandlersQuery({
    pageSize: pagination.rowsPerPage,
    currentPage: pagination.page,
    search: '',
    agencyId: Number(id),
  });

  const handleModalOpen = (nextState: boolean) => () => {
    if (!nextState) {
      setSelectedHandler(undefined);
    }

    setIsModalOpen(nextState);
  };

  const handleEditHandler = (handler: CaseHandlerResponseDto) => () => {
    setSelectedHandler(handler);
    setIsModalOpen(true);
  };

  return (
    <ListLayout padding={0} title={t('caseHandlers.title')} subtitle={t('caseHandlers.subtitle')}>
      <Box
        display="flex"
        alignItems="center"
        gap={(theme) => theme.spacing(3)}
        marginBottom={(theme) => theme.spacing(3)}
      >
        <Typography
          variant="subtitle1"
          align="left"
          color={(theme) => theme.palette.text.primary}
          sx={{
            flexGrow: 1,
          }}
        >
          {data?.count} {t('caseHandlers.results')}
        </Typography>

        <SearchInput placeholder={t('caseHandlers.search')} onSearch={() => {}} />
        <ToggleButtonGroup
          exclusive
          sx={{
            height: (theme) => theme.spacing(5),
            '& button': {
              padding: (theme) => theme.spacing(1),
              width: (theme) => theme.spacing(5),
            },
          }}
        >
          <ToggleButton onClick={handleModalOpen(true)} value="add" aria-label="right aligned">
            <Add />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {isModalOpen && (
        <HandlerModal
          open={isModalOpen}
          onClose={handleModalOpen(false)}
          agencyId={id || ''}
          handler={selectedHandler}
        />
      )}

      {data?.count !== undefined && data.count > 0 && (
        <Table
          height={TABLE_HEIGHT_WITH_AGENCIES_LAYOUT}
          columns={[
            {
              id: 'name',
              label: t('caseHandlers.table.name'),
              align: 'left',
            },
            {
              id: 'description',
              label: t('caseHandlers.table.description'),
              align: 'left',
            },
            {
              id: 'enabled',
              label: t('caseHandlers.table.enabled'),
              align: 'left',
            },
            {
              id: 'scanWorkflow',
              label: t('caseHandlers.table.scanWorkflow'),
              align: 'left',
            },
            {
              id: 'scanStep',
              label: t('caseHandlers.table.scanStep'),
              align: 'left',
            },
            {
              id: 'scanStatus',
              label: t('caseHandlers.table.scanStatus'),
              align: 'left',
            },
            {
              id: 'nextWorkflow',
              label: t('caseHandlers.table.nextWorkflow'),
              align: 'left',
            },
            {
              id: 'nextStep',
              label: t('caseHandlers.table.nextStep'),
              align: 'left',
            },
            {
              id: 'nextStatus',
              label: t('caseHandlers.table.nextStatus'),
              align: 'left',
            },
            {
              id: 'channel',
              label: t('caseHandlers.table.channel'),
              align: 'left',
            },
            { id: 'actions', label: '', align: 'left' },
          ]}
          data={{
            key: 'id',
            items: data.items.map((item) => ({
              ...item,
              name: item.name,
              description: item.description,
              enabled: item.enabled ? <Check color="success" /> : <Close color="error" />,
              scanWorkflow: item.scanWorkflowName,
              scanStep: item.scanStepName,
              scanStatus: item.scanStatus ? t(`cases.status.${item.scanStatus}`) : '',
              nextWorkflow: item.nextWorkflowName,
              nextStep: item.nextStepName,
              nextStatus: item.nextStatus ? t(`cases.status.${item.nextStatus}`) : '',
              channel: item.channel,
              actions: (
                <IconButton onClick={handleEditHandler(item)}>
                  <Edit color="primary" />
                </IconButton>
              ),
            })),
            totalCount: data.count,
          }}
          pagination={pagination}
          stickyHeader
        />
      )}
    </ListLayout>
  );
};

export default IaCaseEventHandlersList;
